/**
 * EmailSendLogSection — Admin -> Admin -> Emails -> Recent Sends.
 * Read-only history of every automated-email send attempt so admins can
 * confirm delivery without checking Resend directly.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getApiUrl } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Loader2, History, RefreshCw, CheckCircle2, XCircle } from "lucide-react";

const API = getApiUrl();
const PAGE_SIZE = 20;

export default function EmailSendLogSection() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = async (p = page) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/email-send-log`, {
        params: { limit: PAGE_SIZE, skip: p * PAGE_SIZE },
        withCredentials: true,
      });
      setEntries(data.entries || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error("Failed to load send history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(0); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const changePage = (next) => {
    setPage(next);
    load(next);
  };

  return (
    <div className="space-y-3" data-testid="email-send-log-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <History size={18} /> Recent Sends
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Every automated email attempt, newest first — confirms whether it actually sent.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="rounded-none"
          onClick={() => load(page)}
          data-testid="email-send-log-refresh-btn"
        >
          <RefreshCw size={14} className="mr-1.5" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin" /></div>
      ) : entries.length === 0 ? (
        <div className="text-center py-10 text-sm text-muted-foreground border border-dashed rounded-sm" data-testid="email-send-log-empty">
          No emails have been sent yet.
        </div>
      ) : (
        <div className="border border-border rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left">
                <th className="p-3 font-medium">When</th>
                <th className="p-3 font-medium">Event</th>
                <th className="p-3 font-medium">To</th>
                <th className="p-3 font-medium">Subject</th>
                <th className="p-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.log_id} className="border-t border-border" data-testid={`email-send-log-row-${e.log_id}`}>
                  <td className="p-3 text-muted-foreground whitespace-nowrap">
                    {e.sent_at ? new Date(e.sent_at).toLocaleString() : "—"}
                  </td>
                  <td className="p-3">
                    {e.event_label || e.event}
                    {e.is_test && <span className="ml-1.5 text-[10px] uppercase text-muted-foreground border border-border rounded px-1 py-0.5">test</span>}
                  </td>
                  <td className="p-3 text-muted-foreground truncate max-w-[220px]">
                    {(e.to || []).join(", ")}
                  </td>
                  <td className="p-3 text-muted-foreground truncate max-w-[260px]">{e.subject}</td>
                  <td className="p-3">
                    {e.status === "sent" ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 text-xs" data-testid={`email-send-log-status-${e.log_id}`}>
                        <CheckCircle2 size={14} /> Sent
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 text-red-600 text-xs"
                        title={e.error || ""}
                        data-testid={`email-send-log-status-${e.log_id}`}
                      >
                        <XCircle size={14} /> Failed
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
          <span>
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline" size="sm" className="rounded-none h-7"
              disabled={page === 0}
              onClick={() => changePage(page - 1)}
              data-testid="email-send-log-prev-btn"
            >
              Previous
            </Button>
            <Button
              variant="outline" size="sm" className="rounded-none h-7"
              disabled={(page + 1) * PAGE_SIZE >= total}
              onClick={() => changePage(page + 1)}
              data-testid="email-send-log-next-btn"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
